import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from "react-qr-code";
import html2canvas from 'html2canvas';
import apiService from '../services/apiService';

interface ReservationSuccessProps {
  serviceDetails: any; // Updated to accept full reservation data
}

const ReservationSuccess: React.FC<ReservationSuccessProps> = ({ serviceDetails }) => {
  const { t } = useTranslation();
  const invoiceRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [reservationDetails, setReservationDetails] = useState<any>(null);

  useEffect(() => {
    const fetchReservationDetails = async () => {
      try {
        if (serviceDetails?.orderNumber || serviceDetails?.data?.reservation?.order_number) {
          const orderNumber = serviceDetails.orderNumber || serviceDetails.data.reservation.order_number;
          const response = await apiService.getReservation(orderNumber);
          
          if (response.success) {
            setReservationDetails(response.data.reservation);
          } else {
            setError(t('success.loadError'));
          }
        } else {
          // If details are already passed in
          setReservationDetails(serviceDetails.data?.reservation || serviceDetails);
        }
      } catch (err) {
        console.error('Error fetching reservation details:', err);
        setError(t('success.loadError'));
      } finally {
        setLoading(false);
      }
    };

    fetchReservationDetails();
  }, [serviceDetails, t]);

  const downloadAsPNG = async () => {
    if (!invoiceRef.current) return;
    
    try {
      setDownloading(true);
      const canvas = await html2canvas(invoiceRef.current);
      const link = document.createElement('a');
      link.download = `facture-${reservationDetails.order_number}.png`;
      link.href = canvas.toDataURL();
      link.click();
    } catch (err) {
      console.error('Error generating invoice image:', err);
      setError(t('success.downloadError'));
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-[#ededed]">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 text-[#2196F3] mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-lg">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-[#ededed]">
        <div className="text-center p-6 bg-white rounded-lg shadow-md max-w-sm">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h3 className="text-xl font-bold mb-2">{t('success.errorTitle')}</h3>
          <p className="mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-[#2196F3] text-white rounded-full shadow-md"
          >
            {t('success.retry')}
          </button>
        </div>
      </div>
    );
  }

  // Ensure we have reservation details
  if (!reservationDetails) {
    return null;
  }

  // Prepare prices from reservation details
  const prices = {
    oil: parseFloat(reservationDetails.oil_price),
    oilFilter: parseFloat(reservationDetails.oil_filter_price),
    airFilter: reservationDetails.service_type === 'complete' ? parseFloat(reservationDetails.air_filter_price || '0') : null,
    fuelFilter: reservationDetails.service_type === 'complete' ? parseFloat(reservationDetails.fuel_filter_price || '0') : null,
    labor: parseFloat(reservationDetails.labor_price),
    travel: parseFloat(reservationDetails.travel_price)
  };

  return (
    <div className="flex flex-col items-center px-4 py-8 max-w-md mx-auto bg-[#ededed] min-h-screen">
      <div className="w-full bg-white rounded-lg border border-gray-200 p-6 mb-1">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">{t('success.congratulations')}</h1>
          <img src="/mascot.png" alt={t('success.mascot')} className="w-32 h-32" />
        </div>
        <p className="text-center mb-4">
          {t('success.reservationSuccessMessage')}
        </p>
        
        <button
          onClick={downloadAsPNG}
          className={`flex items-center px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors mx-auto mt-4 ${downloading ? 'opacity-70 cursor-not-allowed' : ''}`}
          disabled={downloading}
        >
          {downloading ? (
            <>
              <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {t('success.downloading')}
            </>
          ) : (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
              {t('success.downloadInvoice')}
            </>
          )}
        </button>
      </div>

      <div ref={invoiceRef} className="w-full bg-white rounded-lg border border-gray-200 px-6 pb-6 pt-1 mb-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold">{t('success.invoice')}</h2>
          <QRCode value={reservationDetails.order_number} size={64} className='mt-2' />
        </div>
        <div className="mb-4">
          <p><strong>{t('success.orderNumber')}:</strong> {reservationDetails.order_number}</p>
          <p><strong>{t('success.date')}:</strong> {new Date(reservationDetails.scheduled_date).toLocaleDateString()}</p>
          <p><strong>{t('success.time')}:</strong> {reservationDetails.scheduled_time}</p>
          <p><strong>{t('success.location')}:</strong> {reservationDetails.location}</p>
          <p><strong>{t('success.phoneNumber')}:</strong> {reservationDetails.phone}</p>
        </div>
        <div className="mb-4">
          <h3 className="font-semibold mb-2">{t('success.serviceDetails')}</h3>
          <p>
            {reservationDetails.service_type === 'simple' ? t('success.simpleOilChange') : t('success.completeOilChange')}
            {' '}{t('success.for')}{' '}
            {reservationDetails.vehicle ? `${reservationDetails.vehicle.modele} ${reservationDetails.vehicle.annee}` : 'Votre véhicule'}
          </p>
          <p>{t('success.oilBrand')}: {reservationDetails.oil_brand} <b>{reservationDetails.oil_grade} {reservationDetails.oil_capacity}</b></p>
        </div>
        <div className="space-y-2 mb-4">
          <h3 className="font-medium mb-2">{t('success.elements')}</h3>
          {Object.entries(prices).map(([key, value]) => (
            value !== null && (
              <div key={key} className="flex justify-between">
                <span>{t(`success.${key}`)}</span>
                <span>{value.toFixed(2)} MAD</span>
              </div>
            )
          ))}
          <div className="flex justify-between pt-2 border-t border-gray-200 font-semibold">
            <span>{t('success.total')}</span>
            <span>{parseFloat(reservationDetails.total_price).toFixed(2)} MAD</span>
          </div>
        </div>

        <div className="text-center text-sm text-gray-500 mt-6">
          <p>{t('success.thankYou')}</p>
          <p>{t('success.contactInfo')}: +212 608 112626</p>
        </div>
      </div>

      <div className="w-full text-center">
        <div className="text-center mb-6">

        </div>

   
      </div>
    </div>
  );
};

export default ReservationSuccess;